import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { 
  Code, 
  Smartphone, 
  Cloud, 
  Shield, 
  BarChart3, 
  Cpu,
  ArrowRight,
  Check
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const Services = () => {
  const navigate = useNavigate();
  const services = [
    {
      icon: Code,
      title: "Web Development",
      description: "Custom web applications built with modern frameworks and best practices.",
      features: ["React & Next.js", "TypeScript", "Responsive Design", "SEO Optimization"],
      price: "From $5,000",
      color: "from-violet-500 to-purple-600",
    },
    {
      icon: Smartphone,
      title: "Mobile Apps",
      description: "Native and cross-platform mobile applications for iOS and Android.",
      features: ["React Native", "Flutter", "Native iOS/Android", "App Store Optimization"],
      price: "From $10,000",
      color: "from-blue-500 to-cyan-600",
    },
    {
      icon: Cloud,
      title: "Cloud Solutions",
      description: "Scalable cloud infrastructure and migration services.",
      features: ["AWS & Azure", "Kubernetes", "Serverless", "24/7 Monitoring"],
      price: "From $2,000/mo",
      color: "from-emerald-500 to-teal-600",
    },
    {
      icon: Shield,
      title: "Cybersecurity",
      description: "Comprehensive security audits and protection solutions.",
      features: ["Penetration Testing", "Security Audits", "Compliance", "Incident Response"],
      price: "From $3,000",
      color: "from-red-500 to-rose-600",
    },
    {
      icon: BarChart3,
      title: "Data Analytics",
      description: "Transform your data into actionable business insights.",
      features: ["Business Intelligence", "Machine Learning", "Data Visualization", "Predictive Analytics"],
      price: "From $4,000",
      color: "from-pink-500 to-fuchsia-600",
    },
    {
      icon: Cpu,
      title: "AI Integration",
      description: "Leverage artificial intelligence to automate and optimize.",
      features: ["ChatGPT Integration", "Custom AI Models", "Automation", "NLP Solutions"],
      price: "From $8,000",
      color: "from-amber-500 to-orange-600",
    },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-24 relative overflow-hidden px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="absolute inset-0">
            <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto text-center relative z-10">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm font-medium mb-6 animate-fade-in">
              Our Services
            </span>
            <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6 animate-fade-in">
              Solutions That
              <span className="gradient-text block">Drive Growth</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto animate-fade-in">
              From concept to deployment, we offer comprehensive technology services 
              tailored to your business needs and goals.
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="container mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
              {services.map((service, index) => (
                <div
                  key={service.title}
                  className="group relative p-8 rounded-2xl glass-card hover-lift"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors" />
                  
                  <div className="relative z-10">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                      <service.icon className="w-7 h-7 text-white" />
                    </div>
                    
                    <h3 className="text-2xl font-heading font-bold mb-3">{service.title}</h3>
                    <p className="text-muted-foreground mb-6">{service.description}</p>
                    
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-primary" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <span className="text-lg font-semibold gradient-text">{service.price}</span>
                      <Button variant="ghost" size="sm" className="group/btn" onClick={() => navigate("/contact")}>
                        Learn More
                        <ArrowRight className="w-4 h-4 ml-1 group-hover/btn:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="container mx-auto">
            <div className="max-w-3xl mx-auto glass-card rounded-3xl p-8 md:p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
                Ready to Transform Your Business?
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Let's discuss how our services can help you achieve your goals. 
                Schedule a free consultation with our experts today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="glow-primary bg-gradient-to-r from-primary to-accent hover:opacity-90" onClick={() => navigate("/contact")}>
                  Schedule Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="glass" onClick={() => navigate("/blog")}>
                  View Case Studies
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                How We Work
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Our Process</h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                A proven methodology that ensures successful project delivery every time.
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Discovery", desc: "Understanding your needs and goals" },
                { step: "02", title: "Strategy", desc: "Crafting the perfect solution" },
                { step: "03", title: "Development", desc: "Building with precision and care" },
                { step: "04", title: "Launch", desc: "Deploying and optimizing" },
              ].map((item, index) => (
                <div key={item.step} className="text-center relative">
                  {index < 3 && (
                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-primary/50 to-transparent" />
                  )}
                  <div className="relative z-10 w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center glow-primary">
                    <span className="text-white font-bold">{item.step}</span>
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Services;
